import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Flag, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  Eye, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Clock,
  Filter,
  RefreshCw,
  Image,
  X
} from 'lucide-react';
import { fetchPostReports, fetchPostReportCounts, updatePostReportStatus, PostReport, PostReportCounts } from '../services/api.ts';
import { useAuth } from '../context/AuthContext.tsx';
import './ReportedPosts.css';

const ReportedPosts: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState<PostReport[]>([]);
  const [counts, setCounts] = useState<PostReportCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const reportsPerPage = 10;

  const adminEmail = user?.email || '';

  useEffect(() => {
    loadData();
  }, [currentPage, filterStatus, adminEmail]);

  const loadData = async () => {
    if (!adminEmail) {
      setError('Admin authentication required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const offset = (currentPage - 1) * reportsPerPage;
      const statusFilter = filterStatus === 'all' ? undefined : filterStatus;
      
      const [reportsData, countsData] = await Promise.all([
        fetchPostReports(adminEmail, statusFilter, offset, reportsPerPage),
        fetchPostReportCounts(adminEmail)
      ]);
      
      setReports(reportsData);
      setCounts(countsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load reports');
      console.error('Error loading reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (reportId: number, newStatus: 'dismissed' | 'action_taken') => {
    try {
      setActionLoading(reportId);
      await updatePostReportStatus(reportId, newStatus, adminEmail);
      await loadData();
    } catch (err) {
      alert(`Failed to update report: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
      pending: { icon: <Clock size={14} />, className: 'badge-pending', label: 'Pending' },
      reviewed: { icon: <Eye size={14} />, className: 'badge-reviewed', label: 'Reviewed' },
      dismissed: { icon: <XCircle size={14} />, className: 'badge-dismissed', label: 'Dismissed' },
      action_taken: { icon: <CheckCircle size={14} />, className: 'badge-action', label: 'Action Taken' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`status-badge ${badge.className}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const getReasonBadge = (reason: string) => {
    const badges: Record<string, { icon: React.ReactNode; className: string; label: string }> = {
      spam: { icon: <AlertTriangle size={14} />, className: 'reason-spam', label: 'Spam' },
      inappropriate: { icon: <Flag size={14} />, className: 'reason-inappropriate', label: 'Inappropriate' },
      harassment: { icon: <AlertTriangle size={14} />, className: 'reason-harassment', label: 'Harassment' },
      other: { icon: <Flag size={14} />, className: 'reason-other', label: 'Other' },
    };
    const badge = badges[reason] || badges.other;
    return (
      <span className={`reason-badge ${badge.className}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text: string | null, maxLength: number = 100) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  if (loading && reports.length === 0) {
    return (
      <div className="reported-posts-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reported-posts-page">
        <div className="error-container">
          <AlertTriangle size={48} />
          <h3>Error Loading Reports</h3>
          <p>{error}</p>
          <button onClick={loadData} className="retry-button">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reported-posts-page">
      <div className="page-header">
        <div className="header-title">
          <Flag size={28} />
          <h1>Reported Posts</h1>
        </div>
        <button onClick={loadData} className="refresh-button" disabled={loading}>
          <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {counts && (
        <div className="stats-grid">
          <div className="stat-card stat-pending" onClick={() => setFilterStatus('pending')}>
            <Clock size={24} />
            <div className="stat-info">
              <span className="stat-value">{counts.pending}</span>
              <span className="stat-label">Pending</span>
            </div>
          </div>
          <div className="stat-card stat-dismissed" onClick={() => setFilterStatus('dismissed')}>
            <XCircle size={24} />
            <div className="stat-info">
              <span className="stat-value">{counts.dismissed}</span>
              <span className="stat-label">Dismissed</span>
            </div>
          </div>
          <div className="stat-card stat-action" onClick={() => setFilterStatus('action_taken')}>
            <CheckCircle size={24} />
            <div className="stat-info">
              <span className="stat-value">{counts.action_taken}</span>
              <span className="stat-label">Action Taken</span>
            </div>
          </div>
          <div className="stat-card stat-total" onClick={() => setFilterStatus('all')}>
            <Flag size={24} />
            <div className="stat-info">
              <span className="stat-value">{counts.total}</span>
              <span className="stat-label">Total Reports</span>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-group">
          <Filter size={16} />
          <select 
            value={filterStatus} 
            onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">All Reports</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="dismissed">Dismissed</option>
            <option value="action_taken">Action Taken</option>
          </select>
        </div>
      </div>

      {/* Reports Table */}
      <div className="reports-table-container">
        {reports.length === 0 ? (
          <div className="empty-state">
            <Flag size={48} />
            <h3>No Reports Found</h3>
            <p>There are no {filterStatus !== 'all' ? filterStatus.replace('_', ' ') : ''} reports at the moment.</p>
          </div>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Post Content</th>
                <th>Reason</th>
                <th>Reporter</th>
                <th>Post Owner</th>
                <th>Status</th>
                <th>Reported At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.report_id} className={report.status === 'pending' ? 'row-pending' : ''}>
                  <td className="cell-id">#{report.report_id}</td>
                  <td className="cell-content">
                    <div className="content-preview">
                      {truncateText(report.post_content, 80)}
                      {!report.post_is_active && (
                        <span className="post-inactive-badge">Post Removed</span>
                      )}
                    </div>
                    {report.post_media && report.post_media.length > 0 && (
                      <div className="post-media-preview">
                        {report.post_media.slice(0, 3).map((media, index) => (
                          <div 
                            key={index} 
                            className="media-thumbnail"
                            onClick={() => setSelectedImage(media.url)}
                          >
                            {media.type === 'video' ? (
                              <video src={media.url} />
                            ) : (
                              <img src={media.url} alt={`Post media ${index + 1}`} />
                            )}
                          </div>
                        ))}
                        {report.post_media.length > 3 && (
                          <span className="more-media">+{report.post_media.length - 3}</span>
                        )}
                      </div>
                    )}
                    {report.description && (
                      <div className="report-description">
                        <strong>Description:</strong> {truncateText(report.description, 60)}
                      </div>
                    )}
                  </td>
                  <td>{getReasonBadge(report.reason)}</td>
                  <td className="cell-user">{report.reporter_name || 'Unknown'}</td>
                  <td className="cell-user">{report.post_owner_name || 'Unknown'}</td>
                  <td>{getStatusBadge(report.status)}</td>
                  <td className="cell-date">{formatDate(report.created_at)}</td>
                  <td className="cell-actions">
                    {report.status === 'pending' && (
                      <div className="action-buttons">
                        <button
                          className="action-btn btn-dismiss"
                          onClick={() => handleStatusUpdate(report.report_id, 'dismissed')}
                          disabled={actionLoading === report.report_id}
                          title="Dismiss Report"
                        >
                          {actionLoading === report.report_id ? (
                            <RefreshCw size={14} className="spinning" />
                          ) : (
                            <XCircle size={14} />
                          )}
                          Dismiss
                        </button>
                        <button
                          className="action-btn btn-action"
                          onClick={() => handleStatusUpdate(report.report_id, 'action_taken')}
                          disabled={actionLoading === report.report_id}
                          title="Take Action (Remove Post)"
                        >
                          {actionLoading === report.report_id ? (
                            <RefreshCw size={14} className="spinning" />
                          ) : (
                            <CheckCircle size={14} />
                          )}
                          Take Action
                        </button>
                      </div>
                    )}
                    {report.status !== 'pending' && (
                      <span className="reviewed-info">
                        {report.reviewed_by && `By: ${report.reviewed_by}`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {reports.length > 0 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="page-btn"
          >
            <ChevronLeft size={16} />
            Previous
          </button>
          <span className="page-info">Page {currentPage}</span>
          <button 
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={reports.length < reportsPerPage}
            className="page-btn"
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={() => setSelectedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={() => setSelectedImage(null)}>
              <X size={24} />
            </button>
            <img src={selectedImage} alt="Full size" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportedPosts;
